import { useState, useRef, useEffect } from 'react';
import api from '../api';

export default function PromptAssistant({ onApplyPrompt, variablesCSV = [] }) {
  const [open, setOpen]         = useState(false);
  const [mensaje, setMensaje]   = useState('');
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [variables, setVariables] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [historial]);

  const enviar = async () => {
    if (!mensaje.trim() || loading) return;
    const userMsg = mensaje.trim();
    setMensaje('');
    setLoading(true);

    const nuevoHistorial = [...historial, { role: 'user', content: userMsg }];
    setHistorial(nuevoHistorial);

    const varsLista = variables
      ? variables.split(',').map(v => v.trim()).filter(Boolean)
      : variablesCSV;

    try {
      const r = await api.post('/ai/prompt-assistant', {
        mensaje: userMsg,
        historial: nuevoHistorial.slice(-10),
        variables: varsLista,
      });
      setHistorial(prev => [...prev, { role: 'assistant', content: r.data.respuesta }]);
    } catch {
      setHistorial(prev => [...prev, { role: 'assistant', content: '❌ Error conectando con la IA. Intenta de nuevo.' }]);
    } finally {
      setLoading(false);
    }
  };

  const aplicarPrompt = (texto) => {
    // Extraer bloque de código si hay uno
    const match = texto.match(/```[\w]*\n?([\s\S]*?)```/);
    const prompt = match ? match[1].trim() : texto;
    onApplyPrompt(prompt);
    setOpen(false);
  };

  const sugerencias = [
    "Ayúdame a crear un prompt para cobranzas",
    "Crea un prompt para recordatorio de citas médicas",
    "Tengo estas columnas en mi CSV, ¿cómo las uso?",
    "¿Cómo hago para que el agente sea más empático?",
  ];

  return (
    <>
      {/* Botón flotante */}
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
        background: open ? 'rgba(124,58,237,0.3)' : 'rgba(124,58,237,0.15)',
        color: '#a78bfa', fontSize: 12, fontWeight: 600,
        transition: 'all 0.2s',
      }}>
        🤖 Asistente IA
      </button>

      {/* Panel del chat */}
      {open && (
        <div style={{
          position: 'fixed', right: 24, bottom: 24, width: 420, height: 580,
          background: '#0d0d1f', border: '1px solid #7c3aed', borderRadius: 16,
          display: 'flex', flexDirection: 'column', zIndex: 1000,
          boxShadow: '0 20px 60px rgba(124,58,237,0.3)',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid #1a1a2e',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>🤖 Asistente de Prompts</div>
              <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>Powered by DeepSeek</div>
            </div>
            <span onClick={() => setOpen(false)} style={{ color: '#475569', cursor: 'pointer', fontSize: 20 }}>×</span>
          </div>

          {/* Variables CSV */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #1a1a2e' }}>
            <input
              value={variables}
              onChange={e => setVariables(e.target.value)}
              placeholder="Columnas de tu CSV (ej: nombre, deuda, contrato)"
              style={{
                width: '100%', padding: '7px 10px', background: '#111',
                border: '1px solid #1e1e3a', borderRadius: 7, color: '#e2e8f0',
                fontSize: 11, outline: 'none',
              }}
            />
            <div style={{ fontSize: 10, color: '#334155', marginTop: 4 }}>
              Escribe tus columnas para que la IA sepa cómo usarlas
            </div>
          </div>

          {/* Mensajes */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {historial.length === 0 && (
              <div>
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, textAlign: 'center' }}>
                  ¿En qué te ayudo hoy?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sugerencias.map((s, i) => (
                    <button key={i} onClick={() => { setMensaje(s); }} style={{
                      padding: '8px 12px', background: 'rgba(124,58,237,0.08)',
                      border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8,
                      color: '#94a3b8', fontSize: 12, cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.2s',
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {historial.map((msg, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', gap: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: msg.role === 'user' ? 'rgba(74,222,128,0.2)' : 'rgba(124,58,237,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
                }}>
                  {msg.role === 'user' ? '👤' : '🤖'}
                </div>
                <div style={{ maxWidth: '80%' }}>
                  <div style={{
                    padding: '10px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.6,
                    background: msg.role === 'user' ? 'rgba(74,222,128,0.1)' : 'rgba(124,58,237,0.1)',
                    border: msg.role === 'user' ? '1px solid rgba(74,222,128,0.2)' : '1px solid rgba(124,58,237,0.2)',
                    color: '#e2e8f0', whiteSpace: 'pre-wrap',
                    borderBottomRightRadius: msg.role === 'user' ? 2 : 10,
                    borderBottomLeftRadius: msg.role === 'assistant' ? 2 : 10,
                  }}>
                    {msg.content}
                  </div>
                  {msg.role === 'assistant' && (
                    <button onClick={() => aplicarPrompt(msg.content)} style={{
                      marginTop: 6, padding: '4px 10px', borderRadius: 6, border: 'none',
                      background: 'rgba(124,58,237,0.2)', color: '#a78bfa',
                      fontSize: 11, cursor: 'pointer', fontWeight: 600,
                    }}>
                      ↑ Usar este prompt
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(124,58,237,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🤖</div>
                <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', fontSize: 12, color: '#475569' }}>
                  Pensando...
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #1a1a2e', display: 'flex', gap: 8 }}>
            <textarea
              value={mensaje}
              onChange={e => setMensaje(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
              placeholder="Escribe tu pregunta... (Enter para enviar)"
              rows={2}
              style={{
                flex: 1, padding: '8px 12px', background: '#111',
                border: '1px solid #1e1e3a', borderRadius: 8, color: '#e2e8f0',
                fontSize: 12, outline: 'none', resize: 'none', fontFamily: 'inherit',
              }}
            />
            <button onClick={enviar} disabled={loading || !mensaje.trim()} style={{
              padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg,#7c3aed,#6d28d9)',
              color: '#fff', fontSize: 16, fontWeight: 700,
              opacity: loading || !mensaje.trim() ? 0.5 : 1,
              transition: 'all 0.2s',
            }}>→</button>
          </div>
        </div>
      )}
    </>
  );
}
